/**
 * A function which adds click detection to document
 * and performs a prescribed action when a click is detected
 * outside of the supllied element.
 * Use an array for params.exceptionClassArray to create exceptions.
 *
 * @param  {Object}  elem A DOM element
 * @param  {Function}  name of class to search for
 * @param  {Object}  an object containing parameters
 */
export function actionOnClickOutside(element, action, params = {}) {
	const exceptionClassArray = params.exceptionClassArray || []
	const useCapture = params.useCapture || false

	const outsideClickListener = (event) => {
		if (
			!element.contains(event.target) &&
			event.target.classList.length &&
			!exceptionClassArray.some((className) =>
				event.target.classList.contains(className)
			)
		) {
			action()
			removeClickListener()
		}
	}

	const removeClickListener = () => {
		document.removeEventListener('click', outsideClickListener, useCapture)
	}

	document.addEventListener('click', outsideClickListener, useCapture)
	element.clearListener = removeClickListener
}

/**
 * Simple function to return an array of sibling elements
 */
export function getSiblings(elem) {
	return Array.prototype.filter.call(
		elem.parentNode.children,
		function (sibling) {
			return sibling !== elem
		}
	)
}

/**
 * Traps the focus to all of the children of an element.
 * When the user presses an Escape key the focus trap should be removed.
 * @param {HTMLElement} element - The element to trap focus in
 * @param {function} [closeFunction] - An optional function to call when the Escape key is pressed
 * @param {FocusOptions} [focusOptions] - Optional focus options to use when setting initial focus
 * @returns {function} A function to remove the focus trap
 */
export function limitKeyboardAccessibility(
	element,
	closeFunction,
	focusOptions = {}
) {
	const trapFocus = (event) => {
		// special case to return when keyhandling is disabled on this element
		if (element.disableKeyHandler) return

		const focusableElsRaw = Array.from(
			element.querySelectorAll(
				'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
			)
		)

		// go through elements and pull out only the ones that are visible
		const focusableEl = focusableElsRaw.filter((el) => {
			const style = getComputedStyle(el)

			return (
				el.offsetParent !== null &&
				style.display !== 'none' &&
				style.visibility !== 'hidden'
			)
		})

		const firstFocusableEl = focusableEl[0]
		const lastFocusableEl = focusableEl[focusableEl.length - 1]

		if (!firstFocusableEl || !lastFocusableEl) {
			console.warn(
				`No focusable elements found in ${JSON.stringify(element)}.`
			)
			return
		}

		if (event.key === 'Escape') {
			removeTrapFocus()
			if (closeFunction) closeFunction()
		} else if (event.key === 'Tab' || event.keyCode === 9) {
			if (event.shiftKey) {
				if (document.activeElement === firstFocusableEl) {
					event.preventDefault()
					lastFocusableEl.focus(focusOptions)
				}
			} else if (
				(document.activeElement.type === 'radio' &&
					document.activeElement.name === lastFocusableEl.name &&
					document.activeElement !== lastFocusableEl) ||
				document.activeElement === lastFocusableEl
			) {
				// If the last focused element is a radio input that is not the `lastFocusableEl` but is part of the radio group that
				// includes the `lastFocusableEl`, do not allow focus to exit the focus trap
				event.preventDefault()
				firstFocusableEl.focus(focusOptions)
			}
		}
	}

	const removeTrapFocus = () => {
		element.removeEventListener('keydown', trapFocus)
	}

	element.addEventListener('keydown', trapFocus)

	element.clearKeyHandler = () => {
		removeTrapFocus()
	}

	return removeTrapFocus
}

/**
 * A function which traverses all parents of a given DOM element
 * and returns the first parent with a class name that matches
 * the supplied class. This matches the JavaScript "closest"
 * function which IE11 does not support.
 *
 * @param  {Object}  elem A DOM element
 * @param  {String}  name of class to search for
 * @return {Object} the closest DOM parent with matching class
 */
export function closestByClass(el, clazz) {
	// Traverse the DOM up with a while loop

	while (!el.classList.contains(clazz)) {
		// Increment the loop to the parent node
		el = el.parentNode
		if (!el || el instanceof HTMLDocument) {
			return null
		}
	}
	// At this point, the while loop has stopped and `el` represents the element that has
	// the class you specified in the second parameter of the function `clazz`

	// Then return the matched element
	return el
}

/**
 * Utility functions for setting and getting cookies
 */
export function setCookie(name, value, days) {
	let expires = ''
	if (days) {
		const date = new Date()
		date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000)
		expires = '; expires=' + date.toUTCString()
	}
	if (typeof value === 'object') {
		// because of Safari bug handling JSON in cookies commas need to be stripped out
		const obj = {}
		for (const [key, item] of Object.entries(value)) {
			if (typeof item === 'string') obj[key] = item.replace(/,/, 'U+0002C')
			else obj[key] = item
		}
		// turn value into JSON
		value = JSON.stringify(obj)
	}
	// create cookie
	document.cookie =
		name + '=' + (value || '') + expires + '; path=/;SameSite=Strict'
}

export function getCookie(name) {
	const nameEQ = name + '='
	const ca = document.cookie.split(';')
	// see if value is JSON
	const isJSON = function isJson(str) {
		try {
			JSON.parse(str)
		} catch (e) {
			return false
		}
		return true
	}
	for (let i = 0; i < ca.length; i++) {
		let c = ca[i]
		while (c.charAt(0) === ' ') c = c.substring(1, c.length)
		if (c.indexOf(nameEQ) === 0) {
			let content = c.substring(nameEQ.length, c.length)
			if (isJSON(content)) {
				content = JSON.parse(content)
				// replace commas
				for (const [key, item] of Object.entries(content)) {
					if (typeof item === 'string') {
						content[key] = item.replace('U+0002C', ',')
					}
				}
			}
			return content
		}
	}
	return null
}

// Get a cookie without an exact name - Adobe cookies are unique to a machine
export function getCookieFuzzy(nameStartsWith, nameEndsWith) {
	const cookies = document.cookie.split(';')

	// Loop through the cookies
	for (let i = 0; i < cookies.length; i++) {
		let cookie = cookies[i]

		// Trim the cookie
		while (cookie.charAt(0) === ' ')
			cookie = cookie.substring(1, cookie.length)

		// Split the cookie name and value
		const cookiePair = cookie.split('=')

		// Check that we got something to work with
		if (cookiePair.length > 1) {
			// Get the cookie name and value
			const cookieName = cookiePair[0]
			const cookieValue = cookiePair[1]

			// Name comparison
			if (
				cookieName.startsWith(nameStartsWith) &&
				cookieName.endsWith(nameEndsWith)
			) {
				return cookieValue
			}
		}
	}
	return null
}

export function eraseCookie(name) {
	document.cookie = name + '=; Max-Age=-99999999;'
}

/**
 * Convert a string to title case
 */
export function toTitleCase(str) {
	return str
		.toLowerCase()
		.split(' ')
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
		.join(' ')
}

/**
 * Validate US zip code
 */
export function validateZipCode(str, allowPlusFour) {
	let regexp
	if (allowPlusFour) regexp = /^[0-9]{5}(?:-[0-9]{4})?$/
	else regexp = /^[0-9]{5}?$/
	if (regexp.test(str)) return true
	else return false
}

/**
 * Returns a function, that, as long as it continues to be invoked, will not
 * be triggered. The function will be called after it stops being called for
 * N milliseconds. If `immediate` is passed, trigger the function on the
 * leading edge, instead of the trailing.
 *
 * @param  {Function} func A function to call after N milliseconds
 * @param  {number} wait The number of milliseconds to wait
 * @param  {boolean} immediate Trigger the function on the leading edge instead of the trailing
 * @return {Function} A function, that, as long as it continues to be invoked, will not be triggered
 */
export function debounce(func, wait, immediate) {
	let timeout
	return function () {
		const context = this
		const args = arguments
		const later = function () {
			timeout = null
			if (!immediate) func.apply(context, args)
		}
		const callNow = immediate && !timeout
		clearTimeout(timeout)
		timeout = setTimeout(later, wait)
		if (callNow) func.apply(context, args)
	}
}

/* function to add smooth scrolling from a click event */
export function scrollAnchors(e, respond = null) {
	e.preventDefault()

	function distanceToTop(el) {
		return Math.floor(el.getBoundingClientRect().top - 150)
	}
	const targetID = respond
		? respond.getAttribute('href')
		: this.getAttribute('href')
	const targetAnchor = document.querySelector(targetID)
	if (!targetAnchor) return
	const originalTop = distanceToTop(targetAnchor)

	// detect if the user is on IE11
	const isIE11 = !!window.MSInputMethodContext && !!document.documentMode
	const agent = window.navigator.userAgent.toLowerCase()
	const isEdge = agent.indexOf('edge') > -1 || agent.indexOf('edg') > -1

	if (isIE11 || isEdge) {
		window.scroll(0, originalTop)
	} else {
		window.scrollBy({
			top: originalTop,
			left: 0,
			behavior: 'smooth'
		})
	}

	const checkIfDone = setInterval(function () {
		if (distanceToTop(targetAnchor) === 0) {
			targetAnchor.tabIndex = '-1'
			targetAnchor.focus()

			// Let's make sure the History API even exists first..
			if ('history' in window) {
				window.history.pushState('', '', targetID)
			} else {
				// Do it the old-fashioned way!
				window.location = targetID
			}

			clearInterval(checkIfDone)
		}
	}, 100)
}

/* get browser window dimentions */
export function viewportSize() {
	const vw = Math.max(
		document.documentElement.clientWidth || 0,
		window.innerWidth || 0
	)
	const vh = Math.max(
		document.documentElement.clientHeight || 0,
		window.innerHeight || 0
	)
	return { width: vw, height: vh }
}

/* test a string for JSON and return if true */
export function tryParseJSON(jsonString) {
	try {
		const o = JSON.parse(jsonString)

		// Handle non-exception-throwing cases:
		// Neither JSON.parse(false) or JSON.parse(1234) throw errors, hence the type-checking,
		// but... JSON.parse(null) returns null, and typeof null === "object",
		// so we must check for that, too. Thankfully, null is falsey, so this suffices:
		if (o && typeof o === 'object') {
			return o
		}
	} catch (e) {}

	return false
}

/* Change scroll position accounting for the header's height */
export function headerOffsetScroll(contentSection) {
	const header = document.querySelector('[data-cmp-is="header"]')
	const offset = contentSection
		? contentSection.getBoundingClientRect().top
		: 0
	const headerHeight = header ? header.getBoundingClientRect().height : 0

	window.scrollTo({
		top: offset - headerHeight,
		behavior: 'smooth'
	})
}

export function noop() {}

/**
 * Checks if a string is a valid comma separated list of words
 * and returns the original word or an array of words
 * @param {*} str
 * @returns
 */
export function parseCommaSeparatedWords(str) {
	return str.includes(',') ? str.split(',') : str
}

/**
 * Capitalize first letter in a string
 * @param {*} str
 * @returns
 */
export function capitalizeFirstLetter(str) {
	return str.charAt(0).toUpperCase() + str.slice(1)
}

/**
 * Sort the keys inside an object and return it
 * @param {object} obj - The object to sort
 * @returns {object} - The sorted object
 */
export function sortObjectKeys(obj) {
	// Check if the input is an object
	if (typeof obj !== 'object' || obj === null) {
		throw new TypeError('Input must be an object')
	}

	// Check if obj is an Array
	if (Array.isArray(obj)) {
		throw new TypeError('Input must not be an array')
	}

	try {
		return Object.keys(obj)
			.sort((a, b) => a.localeCompare(b))
			.reduce((result, key) => {
				result[key] = obj[key]
				return result
			}, {})
	} catch (error) {
		console.error('Error sorting object keys: ', error)
		throw error
	}
}
